import BarChartVertical from '@components/BarChartVertical';
import { useTheme } from '@simplywallst/ui-core';
import { useMemo } from 'react';
import Media from 'react-media';
import type { HeadingElements } from '@/utilities/heading';

export interface Props {
  companyName: string;
  industryName: string;
  marketName: string;
  dividendYield: number | null;
  dividendYieldFuture: number | null;
  marketDividendYieldP25: number;
  marketDividendYieldP75: number;
  industryDividendYield: number;
  dividendYieldFormatted: string;
  marketDividendYieldP25Formatted: string;
  marketDividendYieldP75Formatted: string;
  industryDividendYieldFormatted: string;
  dividendYieldFutureFormatted: string;
  labels?: {
    key0: string;
    key1: string;
    key2: string;
    key3: string;
    key4: string;
    tooltipKey4: string;
    bottomLabel: string;
  };
  titleElement?: HeadingElements;
}

const keys = [
  'Company',
  'Market Bottom 25%',
  'Market Top 25%',
  'Industry Average',
  'Forecast in 3 Years',
];

const DividendYieldVsMarket = ({
  companyName,
  industryName,
  marketName,
  dividendYield,
  dividendYieldFuture,
  marketDividendYieldP25,
  marketDividendYieldP75,
  industryDividendYield,
  dividendYieldFormatted,
  marketDividendYieldP25Formatted,
  marketDividendYieldP75Formatted,
  industryDividendYieldFormatted,
  dividendYieldFutureFormatted,
  labels = {
    key0: keys[0],
    key1: keys[1],
    key2: keys[2],
    key3: keys[3],
    key4: keys[4],
    tooltipKey4: `Analyst forecast consensus for ${companyName}'s dividend in 3 years time`,
    bottomLabel: 'Current dividend yield vs market & industry',
  },
  titleElement,
}: Props) => {
  const theme = useTheme();

  const { bottomLabel } = labels;
  const charts = useMemo(() => [bottomLabel], [bottomLabel]);
  const entities = {
    [charts[0]]: {
      [labels.key0]: dividendYield,
      [labels.key1]: marketDividendYieldP25,
      [labels.key2]: marketDividendYieldP75,
      [labels.key3]: industryDividendYield,
      [labels.key4]: dividendYieldFuture,
    },
  };

  const entitiesFormatted = {
    [charts[0]]: {
      [labels.key0]: dividendYieldFormatted,
      [labels.key1]: marketDividendYieldP25Formatted,
      [labels.key2]: marketDividendYieldP75Formatted,
      [labels.key3]: industryDividendYieldFormatted,
      [labels.key4]: dividendYieldFutureFormatted,
    },
  };

  const colors = [
    theme.color.chart01,
    theme.color.chart03RGBA(0.5),
    theme.color.chart03,
    theme.color.chart02,
    theme.color.chart01,
  ];

  const titleColors = [
    theme.color.white,
    theme.color.white,
    theme.color.white,
    theme.color.spaceGray,
    theme.color.white,
  ];

  const entityTooltips = {
    [charts[0]]: {
      [labels.key0]: companyName,
      [labels.key1]: marketName,
      [labels.key2]: marketName,
      [labels.key3]: industryName,
      [labels.key4]: labels.tooltipKey4,
    },
  };

  const { key0, key1, key2, key3, key4 } = labels;
  const realKeys = useMemo(() => {
    return [key0, key1, key2, key3, key4];
  }, [key0, key1, key2, key3, key4]);

  const rest = {
    charts: charts,
    entities: entities,
    entitiesFormatted: entitiesFormatted,
    entityTooltips: entityTooltips,
    colors: colors,
    titleColors: titleColors,
    height: 250,
  };

  return (
    <Media query={`(min-width: ${theme.mdMin})`}>
      {(matches) =>
        matches ? (
          <BarChartVertical
            keys={realKeys}
            titleElement={titleElement}
            {...rest}
          />
        ) : (
          <BarChartVertical
            keys={realKeys.slice(0, 4)}
            titleElement={titleElement}
            {...rest}
          />
        )
      }
    </Media>
  );
};

export default DividendYieldVsMarket;
